import { useRef, useState } from 'react';
import { announceDatePopover } from '../../../../../ui/DateTimePopover/popoverBus';
import {
  addReminder,
  hasWhenAvailable,
  REMINDER_TO_ME,
  REMINDER_TRIGGER,
} from '../../../../../../utils/missionReminders';
import { buildReminderUpdate } from '../utils/confirmReminder';
import { hasInstant } from '../utils/reminderLabels';
import { addPickerSession, closedPickerSession, editPickerSession } from '../utils/pickerSession';
import { usePickerPlacement } from './usePickerPlacement';
import { useRemindMeDismiss } from './useRemindMeDismiss';

/** Open/close state and actions for the reminder popover. */
export function useRemindMePicker({
  taskId,
  reminders,
  canEdit,
  isLocked,
  startMs,
  startIsFuture,
  hasStartReminder,
  persist,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [session, setSession] = useState(closedPickerSession);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);
  const anchorRef = useRef(null);
  const { pickerPos, placePicker } = usePickerPlacement(
    pickerOpen,
    session.step,
    anchorRef,
    pickerRef
  );

  const popoverId = `remind-${taskId || 'task'}`;

  const closePicker = () => {
    setPickerOpen(false);
    setSession(closedPickerSession());
  };

  useRemindMeDismiss({
    pickerOpen,
    popoverId,
    closePicker,
    triggerRef,
    pickerRef,
  });

  const chooseWhenAvailable = (e) => {
    e.stopPropagation();
    if (hasWhenAvailable(reminders)) return;
    persist(addReminder(reminders, {
      trigger: REMINDER_TRIGGER.WHEN_AVAILABLE,
      to: REMINDER_TO_ME,
    }));
    closePicker();
  };

  const chooseTimeToStart = (e) => {
    e.stopPropagation();
    if (!startIsFuture) {
      setSession((prev) => ({
        ...prev,
        startHint: Number.isFinite(startMs)
          ? 'The start time has already passed. Set a future start time to use this reminder.'
          : 'There’s no start time. Set a start time first to use this reminder.',
      }));
      return;
    }
    const atIso = new Date(startMs).toISOString();
    if (hasStartReminder || hasInstant(reminders, atIso)) {
      closePicker();
      return;
    }
    persist(addReminder(reminders, {
      trigger: REMINDER_TRIGGER.AT_TIME,
      at: atIso,
      to: REMINDER_TO_ME,
      forStart: true,
    }));
    closePicker();
  };

  const openAt = (anchor, nextSession) => {
    setSession(nextSession);
    anchorRef.current = anchor;
    placePicker();
    announceDatePopover(popoverId);
    setPickerOpen(true);
  };

  const openAdd = () => {
    if (!canEdit) return;
    openAt(triggerRef.current, addPickerSession(isLocked));
  };

  const openPicker = (e) => {
    e.stopPropagation();
    if (!canEdit) return;
    if (pickerOpen && !session.editingId) {
      closePicker();
      return;
    }
    openAt(triggerRef.current, addPickerSession(isLocked));
  };

  const openEditor = (reminder, e) => {
    e.stopPropagation();
    if (!canEdit || reminder.trigger !== REMINDER_TRIGGER.AT_TIME || !reminder.at) return;
    openAt(e.currentTarget, editPickerSession(reminder));
  };

  const confirmPicker = (e) => {
    e.stopPropagation();
    const next = buildReminderUpdate({ reminders, ...session });
    if (!next) return;
    persist(next);
    closePicker();
  };

  const patchSession = (patch) => setSession((prev) => ({ ...prev, ...patch }));

  return {
    pickerOpen,
    triggerRef,
    pickerRef,
    pickerPos,
    session,
    closePicker,
    openPicker,
    openAdd,
    openEditor,
    confirmPicker,
    chooseWhenAvailable,
    chooseTimeToStart,
    setNote: (note) => patchSession({ note }),
    setDraftDate: (draftDate) => patchSession({ draftDate }),
    setPickerMode: (pickerMode) => patchSession({ pickerMode }),
    setRecurDraft: (recurDraft) => patchSession({ recurDraft }),
  };
}
